import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Radar, ArrowRight, Sparkles, MapPin, Gift, Shield, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { ensureAnonUser, setGpsLocal } from "@/lib/anonSession";
import InstallPrompt from "@/components/hola/InstallPrompt";
import WelcomeCarousel from "@/components/hola/WelcomeCarousel";
import { devWarn } from "@/lib/devLog";

/**
 * /join/:merchantId — Landing pública que abre el QR del comercio.
 * Comportamiento:
 * 1) Crea (o reutiliza) la sesión anónima automáticamente.
 * 2) Activa GPS=On por defecto y guarda el `source_merchant_id` en localStorage para atribución.
 * 3) Precarga info del comercio (branded).
 * 4) Ofrece instalación PWA e ir al /radar.
 */
export default function JoinPage() {
  const { merchantId } = useParams();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState(null);
  const [offers, setOffers] = useState([]);
  const [ready, setReady] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pub] = await Promise.all([
          api.get(`/public/merchants/${merchantId}`),
          ensureAnonUser(), // crea o reusa device_id
        ]);
        setMerchant(pub.data.merchant);
        setOffers(pub.data.offers || []);
        // Atribución: recordar qué QR trajo al usuario (para métricas futuras)
        localStorage.setItem("holaradar_source_merchant", merchantId);
        localStorage.setItem("holaradar_source_seen_at", new Date().toISOString());
        // GPS on por defecto (el usuario aún puede apagarlo desde /radar)
        setGpsLocal(true);
      } catch (e) {
        // Merchant no encontrado o inactivo: renderizamos fallback UI ("Comercio no disponible").
        // Se registra en dev para diagnóstico (por si el flujo de QR está mal impreso).
        devWarn("[join.load] merchant not found:", merchantId, e?.response?.status);
      } finally {
        setReady(true);
      }
    })();
  }, [merchantId]);

  const goRadar = async () => {
    setStarting(true);
    navigate("/radar");
  };

  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-b from-[#FFF0F7] to-white">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#FF007A] text-white grid place-items-center mx-auto mb-3">
            <Radar className="w-7 h-7 animate-pulse" />
          </div>
          <div className="font-heading font-extrabold">Activando tu radar...</div>
        </div>
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-b from-[#FFF0F7] to-white px-6">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-neutral-200 text-neutral-500 grid place-items-center mx-auto mb-3">
            <Radar className="w-7 h-7" />
          </div>
          <div className="font-heading text-2xl font-extrabold">Comercio no encontrado</div>
          <p className="text-sm text-neutral-500 mt-2">El QR podría estar dañado o el comercio ya no participa.</p>
          <Button onClick={() => navigate("/scan")} className="mt-6 rounded-full bg-black text-white font-bold" data-testid="join-fallback-btn">
            Activar radar genérico
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF0F7] to-white">
      <div className="max-w-md mx-auto px-6 pt-10 pb-10">
        {/* Branded header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#FF007A]/30 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-[#FF007A] mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Radar activado
          </div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight" data-testid="join-merchant-name">
            {merchant.business_name}
          </h1>
          <p className="text-sm text-neutral-500 mt-1 capitalize">{merchant.category}</p>
          {merchant.address && (
            <div className="inline-flex items-center gap-1.5 text-xs text-neutral-500 mt-2">
              <MapPin className="w-3.5 h-3.5" /> {merchant.address}
            </div>
          )}
        </motion.div>

        {/* Bienvenida emotiva rotante */}
        <div className="mt-6">
          <WelcomeCarousel />
        </div>

        {/* Featured offer teaser */}
        {offers.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-8 rounded-3xl bg-white border-2 border-[#FF007A]/40 shadow-lg shadow-[#FF007A]/10 p-6"
            data-testid="join-featured-offer"
          >
            <div className="flex items-center gap-2 text-xs font-bold uppercase text-[#FF007A]">
              <Gift className="w-3.5 h-3.5" /> Oferta destacada
            </div>
            <div className="font-heading font-extrabold text-xl mt-1">{offers[0].title}</div>
            <p className="text-sm text-neutral-600 mt-1">{offers[0].description}</p>
            {offers[0].discount_text && (
              <div className="mt-3 inline-block text-xs font-bold text-white bg-[#FF007A] px-2.5 py-1 rounded-full">
                {offers[0].discount_text}
              </div>
            )}
            {offers.length > 1 && (
              <div className="mt-3 text-xs text-neutral-500">+{offers.length - 1} oferta{offers.length > 2 ? "s" : ""} adicional{offers.length > 2 ? "es" : ""} activas</div>
            )}
          </motion.div>
        ) : (
          <div className="mt-8 rounded-2xl bg-white border border-neutral-200 p-5 text-center text-sm text-neutral-500" data-testid="join-no-offers">
            Este comercio aún no tiene ofertas activas. Tu radar quedará listo para futuras promos.
          </div>
        )}

        {/* Perks */}
        <div className="mt-6 space-y-2">
          <FeatureRow icon={Radar} text="Radar activo a 50 metros" />
          <FeatureRow icon={Shield} text="Sin registro · Sin datos personales" />
          <FeatureRow icon={Sparkles} text="Chat con IA para dudas al instante" />
        </div>

        <Button
          onClick={goRadar}
          disabled={starting}
          className="mt-8 h-14 w-full rounded-full bg-[#FF007A] hover:bg-[#e5006e] text-white font-bold text-base shadow-lg shadow-[#FF007A]/30"
          data-testid="join-go-radar-btn"
        >
          {starting ? "Abriendo..." : (<>Abrir mi Radar <ArrowRight className="w-5 h-5" /></>)}
        </Button>

        <div className="mt-6">
          <InstallPrompt variant="modal" onDismiss={() => {}} />
        </div>

        <p className="text-xs text-center text-neutral-500 mt-4">
          Al continuar aceptas compartir tu ubicación GPS mientras el radar esté encendido.
        </p>
      </div>
    </div>
  );
}

function FeatureRow({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-sm text-neutral-700 bg-white rounded-xl px-3 py-2 border border-neutral-200">
      <Icon className="w-4 h-4 text-[#FF007A]" />
      <span className="flex-1">{text}</span>
      <Check className="w-4 h-4 text-emerald-500" />
    </div>
  );
}
